import { FC, useEffect, useMemo } from "react";
import {  useNavigate, useSearch} from "react-location";
import { LocationGenerics } from "router/location";
import { useQuery, useReactiveVar } from "@apollo/client";
import {
  dispatchModal,
  useTableData,
  useUrlState,
  wrapClick,
  withPermissions, displayUserName, } from "utils";
import {Avatar, Shimmers, TableComponent, StatusBadge, ActionButton, SearchSelectInput, SummaryTabs} from "components";
import { GET_SUSPECTED_ILLEGALITIES, GET_SUSPECTED_ILLEGALITIES_SUMMARY } from "./apollo";
import CreateSuspectedIllegalityContainer from "./create";
import CancelPrepaidMonitoringServiceOrderContainer from "./cancel";
import ViewPrepaidMonitoringServiceOrderContainer from "./view";

import { PlusIcon } from "@heroicons/react/24/outline";
import _ from "lodash";
import lodash from "lodash";
import UpdateSuspectedIllegalityContainer from "./update";
import { currentConfigVar } from "apollo/cache/config";
import config from "config";
import moment from "moment";
import OfficeHeader from "../../components/layouts/office-calendar-header";

const statuses = [
  { name: "Pending", href: "Pending" },
  { name: "Order Generated", href: "OrderGenerated" },
  { name: "Completed", href: "Completed" },
];

const altStatuses = [
  { name: "Cancelled", href: "Cancelled" },
];

export const SuspectedIllegalityOrigins = ["ConsumptionAnalysis", "TipOff", "AnonymousTipOff", "FieldOperations"]

const searchOptions = [
  {
    label: "Code",
    value: "code",
    min: 4,
  },
  {
    label: "Reporter Name ",
    value: "reportedBy.fullName",
    min: 6,
  },
  {
    label: "Reporter Phone Number ",
    value: "reportedBy.phoneNumber",
    min: 7,
  },
  {
    label: "Location Address",
    value: "location.address",
    min: 4,
  },
  {
    label: "Location Community",
    value: "location.community",
    min: 4,
  },
  {
    label: "Ghana Post Address",
    value: "ghanaPostAddress",
    min: 5,
  },
  {
    label: "Suspected Culprit Name ",
    value: "suspectedCulprit.fullName",
    min: 6,
  },
  {
    label: "Suspected Culprit Phone Number ",
    value: "suspectedCulprit.phoneNumber",
    min: 7,
  },
];

export const SuspectedIllegalitiesPage: FC = () => {
  const { dateFormat } = useReactiveVar(currentConfigVar);

  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [modal, setModal] = useUrlState<string | undefined>("modal");
  const [origin, setOrigin] = useUrlState("origin");

  const filter = useMemo(
    () => ({
      page: Number(searchParams.page || config.constants.page),
      pageSize: Number(searchParams.pageSize || config.constants.pageSize),
      search: searchParams.search || undefined,
      searchFields: lodash.map(searchOptions, "value"),
      sort: searchParams.sort || "",
      fromDate: searchParams.fromDate || undefined,
      toDate: searchParams.toDate || undefined,
      region: searchParams.region || undefined,
      district: searchParams.district || undefined,
      status: searchParams.status || undefined,
      origin: searchParams.origin || undefined,
      source: "SupportConsole",
    }),
    [searchParams],
  );

  const { data, loading, networkStatus, refetch } = useQuery(GET_SUSPECTED_ILLEGALITIES,
    {
      variables: filter,
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        refetchSummary?.();
      },
    },
  );

  const { data: dataSummary, refetch: refetchSummary } = useQuery(
    GET_SUSPECTED_ILLEGALITIES_SUMMARY,
    {
      variables: filter,
      notifyOnNetworkStatusChange: true,
    },
  );

  const records = useTableData(data);

  const onSearch = (searchValue: string) => {
    navigate({
      search: (old) => ({
        ...old,
        page: Number(config.constants.page || 1),
        pageSize: Number(searchParams.pageSize || config.constants.page),
        search: searchValue?.toString(),
      }),
    });
  };

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.page ? { page: config.constants.page } : {}),
        ...(!old?.pageSize ? { pageSize: config.constants.pageSize } : {}),
        ...(!old?.search ? {} : { search: "" }),
        ...(!old?.sort ? { sort: "-createdAt" } : {}),
      }),
    });
  }, [navigate]);

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <OfficeHeader
        defaultView="month"
        renderActions={() => (
          <>
            {withPermissions(["Super", "Admin"])(
              <>
                <button
                  type="button"
                  onClick={wrapClick(() => {setModal("create")})}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                  Report Suspected Illegality
                </button>
              </>
            )}
          </>
        )}
      />
      <SummaryTabs
        tabs={statuses}
        altTabs={altStatuses}
        summary={dataSummary?.summary}
        param={"status"}
        label="Illegalities"
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title={"suspected illegalities"}
            refetch={refetch}
            isRefetching={loading && networkStatus === 4}
            loading={loading && ![4, 6].includes(networkStatus)}
            data={records}
            onSearchClicked={onSearch}
            hasSearch={true}
            renderHeaderItems={() => (
              <div className="mt-4 sm:mt-0.5 sm:ml-16 sm:flex space-x-2">
                <SearchSelectInput
                  id="origin"
                  labelHidden={true}
                  options={SuspectedIllegalityOrigins.map((origin) => ({
                    label: { title: _.startCase(origin) },
                    value: origin,
                  }))}
                  label={""}
                  placeholder="Select Origin"
                  setFieldValue={(_: any, value: string) => setOrigin(value)}
                  values={{ origin }}
                />
              </div>
            )}
            renderColumns={() => (
              <tr>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Code | Date
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Suspected Culprit
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Location
                </th>

                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  GhanaPost Address
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Origin
                </th>
                {
                  !filter.source && (
                    <th
                      scope="col"
                      className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                    >
                      Source
                    </th>
                  )
                }
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Created By
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                ></th>
              </tr>
            )}
            renderLoader={() => (
              <tr>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>

                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.SingleShimmer />
                </td>

                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.SingleShimmer />
                </td>
                {
                  !filter.source && (
                    <td className="px-6 py-4 border-b border-gray-200">
                      <Shimmers.SingleShimmer />
                    </td>
                  )
                }
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>

                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.SingleShimmer />
                </td>

                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.ActionsShimmer actionsCount={2} />
                </td>
              </tr>
            )}
            renderItem={(item) => (
              <tr
                key={item._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                onClick={wrapClick(dispatchModal(item._id, "view"))}
              >
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 ">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {item?.code || "N/A"}
                  </div>
                  <div className="font-medium text-gray-500 dark:text-gray-400">
                    {moment(item?.createdAt).format(dateFormat)}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <Avatar
                        disabled={true}
                        alt={item?.suspectedCulprit?.fullName || "N A"}
                        src={item?.suspectedCulprit?.profileImageUrl || ""}
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-gray-900 dark:text-gray-100">
                        {item?.suspectedCulprit?.fullName || "Unavailable"}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {item?.suspectedCulprit?.phoneNumber || "N/A"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="text-gray-900 dark:text-gray-100">
                    {item?.location?.address || "N/A"}
                  </div>
                  <div className="text-gray-500 dark:text-gray-500">
                    {item?.location?.community || "N/A"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="text-gray-900 dark:text-gray-100">
                    {item?.ghanaPostAddress || "N/A"}
                  </div>
                </td>

                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <StatusBadge
                    status={item?.origin}
                    options={{
                      AnonymousTipOff: "bg-cyan-100 text-cyan-800",
                      TipOff: "bg-blue-100 text-blue-800",
                      ConsumptionAnalysis: "bg-yellow-100 text-yellow-800",
                      FieldOperations: "bg-green-100 text-green-800",
                    }}
                  />
                </td>
                {
                  !filter.source && (
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                      <StatusBadge
                        status={item?.source === 'IPSConsole' ? "IPS Console" : lodash.startCase(item?.source)}
                        disableStartCase={true}
                        options={{
                          "IPS Console": "bg-cyan-100 text-cyan-800",
                          "Commercial Console": "bg-blue-100 text-blue-800",
                          "Support Console": "bg-yellow-100 text-yellow-800",
                          "Zeus App": "bg-green-100 text-green-800",
                        }}
                      />
                    </td>
                  )
                }
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <Avatar
                        disabled={true}
                        alt={displayUserName(item?.createdBy) || "N A"}
                        src={item?.createdBy?.profileImageUrl || ""}
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-gray-900 dark:text-gray-100">
                        {displayUserName(item?.createdBy)}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {item?.createdBy?.phoneNumber || "N/A"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <StatusBadge
                    status={item?.status}
                    options={{
                      Pending: "bg-gray-100 text-gray-800",
                      NoticeGenerated: "bg-orange-100 text-orange-800",
                      OrderGenerated: "bg-yellow-100 text-yellow-800",
                      Cancelled: "bg-red-100 text-red-800",
                      Completed: "bg-green-100 text-green-800",
                    }}
                  />
                </td>

                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                  <div className="space-x-1">
                    {/*
                      We cannot allow updates after an order has been generated.
                      We use the suspectedIllegality details to prefill the confirmation order details,
                      so the values have to check out.
                     */}
                    {(item?.status === "Pending") && withPermissions(["Super", "Admin"])(
                      <ActionButton
                        action="update"
                        onClick={dispatchModal(item?._id, "update")}
                      />
                    )}
                    <ActionButton
                      action="view"
                      onClick={dispatchModal(item?._id, "view")}
                    />
                    {(item?.status === "Pending") && withPermissions(["Super", "Admin"])(
                      <ActionButton
                        action="cancel"
                        onClick={dispatchModal(item?._id, "cancel")}
                      />
                    )}
                  </div>
                </td>
              </tr>
            )}
          />
        </div>
      </div>

      <CreateSuspectedIllegalityContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
        refetch={refetch}
      />
      {Boolean(searchParams.id?.length) && (
        <>
          <UpdateSuspectedIllegalityContainer
            open={modal === "update"}
            setOpen={(val: boolean) => setModal(val ? "update" : undefined)}
            refetch={refetch}
          />
          <CancelPrepaidMonitoringServiceOrderContainer
            open={modal === "cancel"}
            setOpen={(val: boolean) => setModal(val ? "cancel" : "view")}
            refetch={refetch}
          />
          <ViewPrepaidMonitoringServiceOrderContainer
            open={modal === "view"}
            setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
          />
        </>
      )}
    </main>
  );
};

export default SuspectedIllegalitiesPage;